// The user's latest front-view photograph is stored byte-for-byte, not redrawn.
// Only mesh UVs select a rectangular label area; no pixels/specifications are edited.
export const pumpNameplatePhoto = Object.freeze({
  path: "/feeder-assets/mrs-whisper-powerpump-ultimate-nameplate-front-original.png",
  sha256: "44e089d3dc35de0e96fae237d9f6676ae4c3d0641d31db5b6aaef20af6841e8b",
  width: 334, height: 428,
  crop: Object.freeze({ left: 35, top: 27, width: 262, height: 363 }),
});
export const pumpNameplateNotice = "Originalfoto des Pumpentypenschilds, nur als visuelle Referenz. Die begrenzte Bildauflösung bleibt erhalten; keine verlässlich lesbare oder zertifizierte Spezifikationsquelle. Labelgröße und Lage im Modell sind schematisch.";

const photoAngle = (.83 * pumpNameplatePhoto.crop.width / pumpNameplatePhoto.crop.height) / .377;
const photoCenterAngle = .65;
export const pumpNameplateLayout = Object.freeze({
  radius: .377, length: .83,
  position: Object.freeze([-.14, .54, 0]),
  backingRadius: .375, backingStart: photoCenterAngle - photoAngle / 2 - .025, backingAngle: photoAngle + .05,
  // Preserve the selected photo's aspect on the unrolled surface. These are
  // drawing units, not measured label or pump dimensions.
  photoAngle, photoCenterAngle,
});

function localPhotoUrl(value) {
  const base = typeof window !== "undefined" ? window.location.href : "http://localhost/";
  const url = new URL(value, base), origin = new URL(base).origin;
  if (typeof value !== "string" || url.origin !== origin || !url.pathname.endsWith(pumpNameplatePhoto.path)
      || url.username || url.password || url.search || url.hash) throw new Error("Pump nameplate requires the local original photo");
  return url.href;
}

/** The parent registry owns mesh geometry/materials; this helper owns textures. */
export function createPumpNameplate(THREE, { mesh, material }) {
  if (typeof mesh !== "function" || typeof material !== "function") throw new Error("Pump nameplate requires the model resource adapters");
  const root = new THREE.Group(); root.name = "pump-original-photo-nameplate";
  root.userData = { partId: "pump", notice: pumpNameplateNotice, photoSha256: pumpNameplatePhoto.sha256, pixelEditing: false, status: "blank" };
  const backingMaterial = material(0xdce1df, { roughness: .78, metalness: .02 });
  const backing = mesh(new THREE.CylinderGeometry(pumpNameplateLayout.backingRadius, pumpNameplateLayout.backingRadius, pumpNameplateLayout.length, 48, 1, true, pumpNameplateLayout.backingStart, pumpNameplateLayout.backingAngle), backingMaterial, root, pumpNameplateLayout.position);
  backing.name = "pump-nameplate-neutral-backing"; backing.rotation.z = Math.PI / 2;
  const photoMaterial = material(0xffffff, { roughness: .78, metalness: 0, toneMapped: false });
  // Face the front/upper quarter visible in the normal pump close-up. The narrow
  // backing follows the photograph, rather than implying a large blank label.
  const geometry = new THREE.CylinderGeometry(pumpNameplateLayout.radius, pumpNameplateLayout.radius, pumpNameplateLayout.length, 48, 1, true, pumpNameplateLayout.photoCenterAngle - pumpNameplateLayout.photoAngle / 2, pumpNameplateLayout.photoAngle);
  const uv = geometry.getAttribute("uv"), crop = pumpNameplatePhoto.crop;
  for (let i = 0; i < uv.count; i += 1) {
    const around = uv.getX(i), along = uv.getY(i);
    // Photo height follows the motor axis; its width follows the cylinder arc.
    // Cylinder Y becomes world -X. This rotates the portrait with the installed
    // horizontal motor without mirroring or stretching the photographed text.
    uv.setXY(i, (crop.left + around * crop.width) / pumpNameplatePhoto.width,
      1 - (crop.top + crop.height) / pumpNameplatePhoto.height + along * crop.height / pumpNameplatePhoto.height);
  }
  uv.needsUpdate = true;
  const surface = mesh(geometry, photoMaterial, root, pumpNameplateLayout.position);
  surface.name = "pump-original-photo-curved-label"; surface.rotation.z = Math.PI / 2; surface.visible = false;
  surface.userData = { partId: "pump", notice: pumpNameplateNotice, imageCropOnly: true };

  let disposed = false, generation = 0, status = "blank";
  const textures = new Set(), disposedTextures = new WeakSet();
  const discard = texture => {
    if (!texture || typeof texture.dispose !== "function" || disposedTextures.has(texture)) return;
    disposedTextures.add(texture); textures.delete(texture); texture.dispose();
  };
  const blank = () => {
    surface.visible = false; photoMaterial.map = null; photoMaterial.needsUpdate = true;
    [...textures].forEach(discard);
  };
  const setStatus = value => { status = value; root.userData.status = value; };

  function loadTexture({ url = pumpNameplatePhoto.path, onChange = () => {}, loader } = {}) {
    if (disposed) return false;
    const sourceUrl = localPhotoUrl(url);
    const current = ++generation;
    blank(); setStatus("loading");
    const fail = () => {
      if (disposed || current !== generation) return;
      generation += 1; blank(); setStatus("unavailable"); onChange(status);
    };
    const loaded = texture => {
      if (disposed || current !== generation) { discard(texture); return; }
      if (!texture?.isTexture) { fail(); return; }
      textures.add(texture);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.flipY = true;
      texture.wrapS = THREE.ClampToEdgeWrapping; texture.wrapT = THREE.ClampToEdgeWrapping;
      texture.minFilter = THREE.LinearMipmapLinearFilter; texture.magFilter = THREE.LinearFilter;
      texture.needsUpdate = true;
      photoMaterial.map = texture; photoMaterial.needsUpdate = true; surface.visible = true;
      setStatus("ready"); onChange(status);
    };
    try {
      const request = (loader || new THREE.TextureLoader()).load(sourceUrl, loaded, undefined, fail);
      // TextureLoader returns a texture immediately, including while the image is
      // pending. Retain it so close/error/reload can release that allocation too.
      if (request?.isTexture) {
        if (disposed || current !== generation) discard(request);
        else textures.add(request);
      }
    } catch (_) { fail(); }
    return true;
  }

  return {
    root, surface, backing, loadTexture,
    get status() { return status; },
    dispose() {
      if (disposed) return;
      disposed = true; generation += 1; blank(); setStatus("disposed");
      // Geometry and both materials remain owned by the caller's registry.
    },
  };
}
